import { buildStudentUniversityProfileUrl } from './studentUniversityLinks'

const MESSAGE_MAX_LENGTH = 1000
const RECOMMENDATION_SCHEMA_VERSION = 1
const RECOMMENDATION_SOURCE = 'counselor-research'
const DESCRIPTION_MAX_CHARS = 140
const IMMIGRATION_MAX_CHARS = 120
const PREVIEW_LIST_VISIBLE = 3

const DEFAULT_CLOSING =
	'Please review them before our next counselling session. We can discuss fit, required skills and next steps together.\n\nBest regards,\nYour Career Consultant'

export function buildDefaultRecommendationClosing(consultantName = '') {
	const signature = String(consultantName || '').trim() || 'Your Career Consultant'
	return DEFAULT_CLOSING.replace('Your Career Consultant', signature)
}

export function buildDefaultRecommendationIntro(studentName = '') {
	const firstName = String(studentName || '')
		.trim()
		.split(/\s+/)[0]
	const greeting = firstName ? `Hi ${firstName},` : 'Hi,'
	return `${greeting}\n\nI reviewed your profile and shortlisted these options for you.`
}

/** Full editable message body (greeting + closing). Auto blocks append after this. */
export function buildDefaultRecommendationBody(
	studentName = '',
	consultantName = '',
) {
	return `${buildDefaultRecommendationIntro(studentName)}\n\n${buildDefaultRecommendationClosing(consultantName)}`
}

function asString(value) {
	if (value == null) return ''
	return String(value)
}

function asStringArray(value) {
	if (!Array.isArray(value)) return []
	return value
		.map(entry => {
			if (typeof entry === 'string') return entry.trim()
			if (entry && typeof entry === 'object' && entry.name != null) {
				return String(entry.name).trim()
			}
			return String(entry || '').trim()
		})
		.filter(Boolean)
}

/** Truncate for card display; metadata keeps full strings. */
export function truncatePreviewText(value, maxChars) {
	const text = asString(value).trim()
	if (!text) return ''
	if (text.length <= maxChars) return text
	return `${text.slice(0, Math.max(0, maxChars - 1)).trimEnd()}…`
}

/**
 * First N labels as comma-separated text; if more remain, append ` +N`.
 * Example: "A, B, C +2"
 */
export function formatPreviewList(values, visibleCount = PREVIEW_LIST_VISIBLE) {
	const list = asStringArray(values)
	if (!list.length) return ''
	const visible = list.slice(0, visibleCount)
	const remaining = list.length - visible.length
	const base = visible.join(', ')
	return remaining > 0 ? `${base} +${remaining}` : base
}

function formatCareerLine(career) {
	const parts = [career?.title || 'Career'].filter(Boolean)
	const meta = []
	if (career?.demand) meta.push(`Demand: ${career.demand}`)
	if (career?.salary) meta.push(`Average salary: ${career.salary}`)
	if (meta.length) return `${parts[0]} — ${meta.join(' · ')}`
	return parts[0]
}

function formatCountryLine(country) {
	const parts = [country?.title || country?.name || 'Country'].filter(Boolean)
	const meta = []
	if (country?.jobs) meta.push(`Jobs: ${country.jobs}`)
	if (country?.salary) meta.push(`Average salary: ${country.salary}`)
	if (meta.length) return `${parts[0]} — ${meta.join(' · ')}`
	return parts[0]
}

function formatCourseLine(course) {
	const parts = [course?.title || 'Course'].filter(Boolean)
	const meta = []
	if (course?.tuition) meta.push(`Tuition: ${course.tuition}`)
	if (course?.duration) meta.push(`Duration: ${course.duration}`)
	if (meta.length) return `${parts[0]} — ${meta.join(' · ')}`
	return parts[0]
}

function getUniversityIds(item) {
	const courses = item?.courses || {}
	const universityId = String(
		courses?.university_id || item?.university_id || item?.id || '',
	).trim()
	const courseId = String(
		item?.course_id || courses?.course_id || courses?.id || '',
	).trim()
	return { universityId, courseId }
}

export function getUniversityRecommendationLabel(item) {
	const courses = item?.courses || {}
	return (
		courses?.course_name ||
		courses?.university_name ||
		item?.title ||
		'University program'
	)
}

function formatUniversityLine(item) {
	const label = getUniversityRecommendationLabel(item)
	const { universityId, courseId } = getUniversityIds(item)
	const url = buildStudentUniversityProfileUrl(universityId, courseId)
	if (!url) return label
	return `${label}\n${url}`
}

function mapCareerItem(career) {
	return {
		id: asString(career?.id),
		title: asString(career?.title) || 'Career',
		demand: asString(career?.demand),
		averageSalary: asString(career?.salary ?? career?.averageSalary),
		description: asString(career?.description),
		skills: asStringArray(career?.skills),
	}
}

function mapCountryItem(country) {
	return {
		id: asString(country?.id || country?.title || country?.name),
		title: asString(country?.title || country?.name) || 'Country',
		jobs: asString(country?.jobs),
		averageSalary: asString(country?.salary ?? country?.averageSalary),
		immigrationSummary: asString(country?.immigrationSummary),
	}
}

function mapCourseItem(course) {
	return {
		id: asString(course?.id || course?.title),
		title: asString(course?.title) || 'Course',
		tuition: asString(course?.tuition),
		duration: asString(course?.duration),
		subjects: asStringArray(course?.subjects),
	}
}

function mapUniversityItem(item) {
	const { universityId, courseId } = getUniversityIds(item)
	const selectionId =
		asString(item?.id) ||
		(universityId || courseId ? `${universityId}::${courseId}` : '')
	return {
		id: selectionId,
		title: getUniversityRecommendationLabel(item),
		universityId,
		courseId,
		url: buildStudentUniversityProfileUrl(universityId, courseId),
	}
}

/**
 * Single source of truth for Preview + Consultant Chat cards.
 * `bodyText` must be the FINAL composer value at Send (or current draft for live Preview).
 */
export function buildRecommendationMetadata({
	bodyText = '',
	includeCareers = false,
	includeCountries = false,
	includeCourses = false,
	includeUniversities = false,
	careers = [],
	countries = [],
	courses = [],
	universities = [],
} = {}) {
	const blocks = []

	if (includeCareers && Array.isArray(careers) && careers.length) {
		blocks.push({
			type: 'careers',
			items: careers.map(mapCareerItem),
		})
	}
	if (includeCountries && Array.isArray(countries) && countries.length) {
		blocks.push({
			type: 'countries',
			items: countries.map(mapCountryItem),
		})
	}
	if (includeCourses && Array.isArray(courses) && courses.length) {
		blocks.push({
			type: 'courses',
			items: courses.map(mapCourseItem),
		})
	}
	if (includeUniversities && Array.isArray(universities) && universities.length) {
		blocks.push({
			type: 'universities',
			items: universities.map(mapUniversityItem),
		})
	}

	return {
		schemaVersion: RECOMMENDATION_SCHEMA_VERSION,
		source: RECOMMENDATION_SOURCE,
		bodyText: asString(bodyText),
		blocks,
	}
}

function isNonEmptyStringArray(value) {
	return Array.isArray(value) && value.every(entry => typeof entry === 'string')
}

function isCareerItem(item) {
	return (
		item &&
		typeof item === 'object' &&
		typeof item.id === 'string' &&
		typeof item.title === 'string' &&
		typeof item.demand === 'string' &&
		typeof item.averageSalary === 'string' &&
		typeof item.description === 'string' &&
		isNonEmptyStringArray(item.skills)
	)
}

function isCountryItem(item) {
	return (
		item &&
		typeof item === 'object' &&
		typeof item.id === 'string' &&
		typeof item.title === 'string' &&
		typeof item.jobs === 'string' &&
		typeof item.averageSalary === 'string' &&
		typeof item.immigrationSummary === 'string'
	)
}

function isCourseItem(item) {
	return (
		item &&
		typeof item === 'object' &&
		typeof item.id === 'string' &&
		typeof item.title === 'string' &&
		typeof item.tuition === 'string' &&
		typeof item.duration === 'string' &&
		isNonEmptyStringArray(item.subjects)
	)
}

function isUniversityItem(item) {
	return (
		item &&
		typeof item === 'object' &&
		typeof item.id === 'string' &&
		typeof item.title === 'string' &&
		typeof item.universityId === 'string' &&
		typeof item.courseId === 'string' &&
		typeof item.url === 'string'
	)
}

function isValidBlock(block) {
	if (!block || typeof block !== 'object' || !Array.isArray(block.items)) {
		return false
	}
	if (!block.items.length) return false
	if (block.type === 'careers') return block.items.every(isCareerItem)
	if (block.type === 'countries') return block.items.every(isCountryItem)
	if (block.type === 'courses') return block.items.every(isCourseItem)
	if (block.type === 'universities') return block.items.every(isUniversityItem)
	return false
}

/**
 * Chat/Preview guard: usable metadata → render bodyText + cards.
 * Anything else → plain-text fallback (must not throw).
 */
export function isValidRecommendationMetadata(metadata) {
	try {
		if (!metadata || typeof metadata !== 'object') return false
		if (metadata.schemaVersion !== RECOMMENDATION_SCHEMA_VERSION) return false
		if (metadata.source !== RECOMMENDATION_SOURCE) return false
		if (typeof metadata.bodyText !== 'string') return false
		if (!Array.isArray(metadata.blocks) || metadata.blocks.length === 0) {
			return false
		}
		return metadata.blocks.every(isValidBlock)
	} catch {
		return false
	}
}

/**
 * Builds the plain-text body the student receives in counselor chat.
 * `intro` is the full counselor-edited body; auto blocks are appended after it.
 * Wave 1: keep existing line format unchanged for student fallback.
 */
export function buildRecommendationMessageText({
	intro,
	includeCareers = false,
	includeCountries = false,
	includeCourses = false,
	includeUniversities = false,
	careers = [],
	countries = [],
	courses = [],
	universities = [],
	closing = '',
} = {}) {
	const sections = []
	const trimmedIntro = String(intro || '').trim()
	if (trimmedIntro) sections.push(trimmedIntro)

	if (includeCareers && careers.length) {
		sections.push(
			[
				'💼 Recommended careers',
				...careers.map(item => formatCareerLine(item)),
			].join('\n'),
		)
	}

	if (includeCountries && countries.length) {
		sections.push(
			[
				'🌍 Recommended countries',
				...countries.map(item => formatCountryLine(item)),
			].join('\n'),
		)
	}

	if (includeCourses && courses.length) {
		sections.push(
			[
				'🎓 Recommended courses',
				...courses.map(item => formatCourseLine(item)),
			].join('\n'),
		)
	}

	if (includeUniversities && universities.length) {
		sections.push(
			[
				'🏫 Recommended universities',
				...universities.map(item => formatUniversityLine(item)),
			].join('\n\n'),
		)
	}

	const trimmedClosing = String(closing || '').trim()
	if (trimmedClosing) sections.push(trimmedClosing)

	return sections.join('\n\n')
}

export {
	MESSAGE_MAX_LENGTH,
	DEFAULT_CLOSING,
	RECOMMENDATION_SCHEMA_VERSION,
	RECOMMENDATION_SOURCE,
	DESCRIPTION_MAX_CHARS,
	IMMIGRATION_MAX_CHARS,
	PREVIEW_LIST_VISIBLE,
}
